<template>
  <ValidationProvider ref="provider" v-slot="{ errors }" :vid="$attrs.vid" :name="$attrs.label" :rules="rules">
    <div class="vee-rich-text">
      <div v-if="$attrs.label" class="vee-rich-text-label" :class="{ 'error--text': errors && errors.length }">
        {{ $attrs.label }}
      </div>
      <v-card outlined class="pa-3 vee-rich-text-card" :class="{ 'vee-rich-text-card--error': errors && errors.length }">
        <div class="d-flex flex-wrap align-center mb-3 toolbar">
          <v-btn small text class="mr-1 mb-1" :input-value="isActive('bold')" @mousedown.prevent="toggleBold">
            <v-icon small>
              {{ icons.mdiFormatBold }}
            </v-icon>
          </v-btn>
          <v-btn small text class="mr-1 mb-1" :input-value="isActive('italic')" @mousedown.prevent="toggleItalic">
            <v-icon small>
              {{ icons.mdiFormatItalic }}
            </v-icon>
          </v-btn>

          <v-menu offset-y bottom left :close-on-content-click="true">
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                small
                outlined
                class="font-size-trigger mb-1"
                @mousedown.stop.prevent="saveSelection"
                v-on="on"
              >
                <span class="mr-1">Font size:</span>
                <strong>{{ currentFontSizeLabel }}</strong>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item
                v-for="option in fontSizeOptions"
                :key="option.value"
                @click="applyFontSize(option.value)"
              >
                <v-list-item-title>{{ option.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <div
          ref="editor"
          class="editor-surface"
          contenteditable="true"
          role="textbox"
          :aria-label="$attrs.label"
          :data-placeholder="$attrs.placeholder || 'Start typing...'"
          @input="handleInput"
          @blur="handleBlur"
          @keydown="handleKeydown"
          @mouseup="saveSelection"
          @keyup="saveSelection"
          @focus="saveSelection"
          @paste="handlePaste"
        />
      </v-card>

      <div v-if="errors && errors.length" class="error--text text-caption mt-2">
        {{ errors[0] }}
      </div>
    </div>
  </ValidationProvider>
</template>

<script>
import { mdiFormatBold, mdiFormatItalic } from '@mdi/js'
import { ValidationProvider } from 'vee-validate'
import { isRichTextEmpty, normalizeRichTextValue, sanitizeRichText } from '@/utils/richText'

const FONT_SIZE_OPTIONS = [
  { text: 'Small', value: '1' },
  { text: 'Normal', value: '3' },
  { text: 'Large', value: '5' },
  { text: 'XL', value: '6' },
]

export default {
  name: 'VeeRichTextEditor',
  components: {
    ValidationProvider,
  },
  props: {
    rules: {
      type: [Object, String],
      default: '',
    },
    // eslint-disable-next-line vue/require-default-prop
    value: {
      type: null,
    },
  },
  data: () => ({
    fontSizeOptions: FONT_SIZE_OPTIONS,
    isInternalUpdate: false,
    savedRange: null,
    currentFontSize: '3',
    typingStyle: {
      bold: false,
      italic: false,
      fontSize: '3',
    },
    icons: {
      mdiFormatBold,
      mdiFormatItalic,
    },
  }),
  computed: {
    currentFontSizeLabel() {
      const current = this.fontSizeOptions.find(option => option.value === this.currentFontSize)

      return current ? current.text : 'Normal'
    },
    validationValue() {
      return isRichTextEmpty(this.value) ? '' : this.value
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        const normalized = normalizeRichTextValue(newVal)
        if (this.$refs.editor && this.$refs.editor.innerHTML !== normalized) {
          this.isInternalUpdate = true
          this.$refs.editor.innerHTML = normalized
          this.$nextTick(() => {
            this.isInternalUpdate = false
          })
        }
        this.syncProviderValue()
      },
    },
  },
  mounted() {
    if (this.$refs.editor) {
      this.$refs.editor.innerHTML = normalizeRichTextValue(this.value)
    }
    this.syncProviderValue()
    document.addEventListener('selectionchange', this.captureSelection)
  },
  beforeDestroy() {
    document.removeEventListener('selectionchange', this.captureSelection)
  },
  methods: {
    captureSelection() {
      if (!this.$refs.editor || !this.$refs.editor.contains(document.activeElement)) {
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      this.savedRange = selection.getRangeAt(0).cloneRange()
    },
    restoreSelection() {
      if (!this.savedRange || !this.$refs.editor) {
        return
      }

      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(this.savedRange)
    },
    runCommand(command, value = null) {
      this.restoreSelection()
      document.execCommand(command, false, value)
      this.handleInput()
      this.$nextTick(() => {
        this.$refs.editor.focus()
      })
    },
    applyFontSize(value) {
      this.currentFontSize = value
      this.typingStyle.fontSize = value
      this.restoreSelection()
      if (this.isSelectionCollapsed()) {
        this.syncTypingMarker()
      } else {
        this.applyFontSizeToSelection(value)
      }
      this.$nextTick(() => {
        this.$refs.editor.focus()
      })
    },
    toggleBold() {
      if (this.isSelectionCollapsed()) {
        this.typingStyle.bold = !this.typingStyle.bold
        this.syncTypingMarker()

        return
      }

      this.restoreSelection()
      document.execCommand('bold', false, null)
      this.handleInput()
      this.$nextTick(() => {
        this.$refs.editor.focus()
      })
    },
    toggleItalic() {
      if (this.isSelectionCollapsed()) {
        this.typingStyle.italic = !this.typingStyle.italic
        this.syncTypingMarker()

        return
      }

      this.restoreSelection()
      document.execCommand('italic', false, null)
      this.handleInput()
      this.$nextTick(() => {
        this.$refs.editor.focus()
      })
    },
    applyFontSizeToSelection(value) {
      if (!this.$refs.editor) {
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      const range = selection.getRangeAt(0)
      if (range.collapsed) {
        return
      }

      const map = {
        1: '10px',
        3: '16px',
        5: '24px',
        6: '32px',
      }
      const fontSize = map[value] || '16px'
      const span = document.createElement('span')
      span.setAttribute('style', `font-size: ${fontSize};`)

      const contents = range.extractContents()
      span.appendChild(contents)
      range.insertNode(span)

      const newRange = document.createRange()
      newRange.selectNodeContents(span)
      selection.removeAllRanges()
      selection.addRange(newRange)

      this.handleInput()
    },
    normalizeEditorContent() {
      if (!this.$refs.editor) {
        return
      }

      this.$refs.editor.innerHTML = sanitizeRichText(this.$refs.editor.innerHTML)
    },
    getTypingStyleString() {
      const map = {
        1: '10px',
        3: '16px',
        5: '24px',
        6: '32px',
      }

      const styles = [`font-size: ${map[this.typingStyle.fontSize] || '16px'};`]
      if (this.typingStyle.bold) {
        styles.push('font-weight: bold;')
      }
      if (this.typingStyle.italic) {
        styles.push('font-style: italic;')
      }

      return styles.join(' ')
    },
    isSelectionCollapsed() {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return true
      }

      return selection.getRangeAt(0).collapsed
    },
    syncTypingMarker() {
      if (!this.$refs.editor) {
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      const range = selection.getRangeAt(0)
      if (!range.collapsed) {
        return
      }

      const container = range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement
        : range.startContainer

      const existingMarker = container && container.closest
        ? container.closest('span.typing-marker')
        : null

      if (existingMarker) {
        existingMarker.setAttribute('style', this.getTypingStyleString())
        if (!existingMarker.innerHTML) {
          existingMarker.innerHTML = '<br>'
        }

        return
      }

      const marker = document.createElement('span')
      marker.className = 'typing-marker'
      marker.setAttribute('style', this.getTypingStyleString())
      marker.innerHTML = '<br>'

      range.insertNode(marker)

      const newRange = document.createRange()
      newRange.setStart(marker, 0)
      newRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(newRange)
    },
    handleInput() {
      if (this.isInternalUpdate || !this.$refs.editor) {
        return
      }

      const html = sanitizeRichText(this.$refs.editor.innerHTML)
      if (isRichTextEmpty(html)) {
        this.isInternalUpdate = true
        this.$refs.editor.innerHTML = ''
        this.$nextTick(() => {
          this.isInternalUpdate = false
        })
        this.$emit('input', '')
        this.syncProviderValue('')
        this.validateProviderValue('')

        return
      }

      if (this.$refs.editor.innerHTML !== html) {
        this.isInternalUpdate = true
        this.$refs.editor.innerHTML = html
        this.$nextTick(() => {
          this.isInternalUpdate = false
        })
      }

      this.$emit('input', html)
      this.syncProviderValue(html)
      this.validateProviderValue(html)
    },
    handleBlur() {
      if (!this.$refs.editor) {
        return
      }

      const html = normalizeRichTextValue(this.$refs.editor.innerHTML)
      this.$emit('input', html)
      this.syncProviderValue(html)
      this.validateProviderValue(html)
    },
    handleKeydown(event) {
      if (event.key === 'Tab') {
        event.preventDefault()
        this.insertPlainText('  ')

        return
      }

      if ((event.key === 'Backspace' || event.key === 'Delete') && this.handleEmptyBlockRemoval(event)) {
        return
      }

      if (event.key === 'Enter' && this.hasActiveTypingStyle()) {
        event.preventDefault()
        this.insertStyledLineBreak()
      }
    },
    handleEmptyBlockRemoval(event) {
      if (!this.$refs.editor) {
        return false
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return false
      }

      const range = selection.getRangeAt(0)
      if (!range.collapsed) {
        return false
      }

      const block = this.getCurrentBlock(range)
      if (!block || !this.isBlockEmpty(block)) {
        return false
      }

      event.preventDefault()

      const previousBlock = block.previousElementSibling
      const nextBlock = block.nextElementSibling
      block.remove()

      const targetBlock = previousBlock || nextBlock
      const newRange = document.createRange()

      if (targetBlock) {
        if (targetBlock.childNodes.length > 0) {
          newRange.setStart(targetBlock, targetBlock.childNodes.length)
        } else {
          newRange.setStart(targetBlock, 0)
        }
      } else {
        newRange.setStart(this.$refs.editor, 0)
      }

      newRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(newRange)
      this.$nextTick(() => {
        this.$refs.editor.focus()
      })

      return true
    },
    getCurrentBlock(range) {
      const container = range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement
        : range.startContainer

      if (!container || !container.closest) {
        return null
      }

      return container.closest('div, p')
    },
    isBlockEmpty(block) {
      if (!block) {
        return false
      }

      const text = (block.textContent || '')
        .replace(/\u200B/g, '')
        .trim()

      const hasOnlyBreaks = Array.from(block.childNodes).every(node => node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR')

      return text.length === 0 || hasOnlyBreaks
    },
    hasActiveTypingStyle() {
      return this.typingStyle.bold || this.typingStyle.italic || this.typingStyle.fontSize !== '3'
    },
    insertStyledLineBreak() {
      if (!this.$refs.editor) {
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      const range = selection.getRangeAt(0)
      if (!range) {
        return
      }

      const block = document.createElement('div')
      block.setAttribute('style', this.getTypingStyleString())
      block.innerHTML = '<br>'

      range.deleteContents()
      range.insertNode(block)

      const newRange = document.createRange()
      newRange.setStart(block, 0)
      newRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(newRange)
    },
    handlePaste(event) {
      event.preventDefault()
      const text = (event.clipboardData || window.clipboardData).getData('text/plain')
      this.insertPlainText(text)
    },
    insertPlainText(text) {
      if (!this.$refs.editor) {
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      const range = selection.getRangeAt(0)
      range.deleteContents()

      const textNode = document.createTextNode(String(text || '').replace(/\u00a0/g, ' '))
      range.insertNode(textNode)

      const newRange = document.createRange()
      newRange.setStartAfter(textNode)
      newRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(newRange)

      this.handleInput()
    },
    saveSelection() {
      this.captureSelection()
    },
    syncProviderValue(value = this.validationValue) {
      if (this.$refs.provider && typeof this.$refs.provider.syncValue === 'function') {
        this.$refs.provider.syncValue(value)
      }
    },
    validateProviderValue(value = this.validationValue) {
      this.$nextTick(() => {
        if (this.$refs.provider && typeof this.$refs.provider.validate === 'function') {
          this.$refs.provider.validate(value)
        }
      })
    },
    isActive(command) {
      if (!document.queryCommandState) {
        return false
      }

      try {
        return document.queryCommandState(command)
      } catch (error) {
        return false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.vee-rich-text {
  .toolbar {
    gap: 8px;
  }

  .vee-rich-text-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    line-height: 1;
    margin-bottom: 8px;
  }

  .font-size-trigger {
    min-width: 0;
    width: auto;
  }

  .editor-surface {
    min-height: 180px;
    outline: none;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 6px;
    line-height: 1.2;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .editor-surface p,
  .editor-surface div {
    margin: 0;
  }

  .editor-surface:empty::before {
    content: attr(data-placeholder);
    color: rgba(255, 255, 255, 0.45);
    pointer-events: none;
  }

  .vee-rich-text-card--error {
    border-color: var(--v-error-base) !important;
  }
}
</style>
